import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { isEnglishRoute } from '../lib/i18n';
import { fetchLocalizedCollection } from '../lib/cms';
import { useNavigationMenu, getMenuHref, getMenuTitle } from '../lib/useNavigationMenu';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [lubricationOpen, setLubricationOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  const location = useLocation();
  const navRef = useRef(null);
  const isEn = isEnglishRoute(location.pathname);
  const { structuredProductMenu, productItems, aboutItems } = useNavigationMenu();

  const { alphabeticalProducts, bottomProducts } = useMemo(() => {
    const regular = [];
    const bottom = [];
    productItems.forEach((item) => {
      const sti = String(item.sti || '').toLowerCase();
      const rawTitel = String(
        typeof item.titel === 'object' ? item.titel?.da || Object.values(item.titel)[0] || '' : item.titel || ''
      ).toLowerCase();
      const isBottom =
        sti.includes('reservedele') ||
        sti.includes('spare-parts') ||
        sti.includes('brugte') ||
        sti.includes('used') ||
        rawTitel.includes('reservedele') ||
        rawTitel.includes('spare parts') ||
        rawTitel.includes('brugte') ||
        rawTitel.includes('used');
      if (isBottom) {
        bottom.push(item);
      } else {
        regular.push(item);
      }
    });

    regular.sort((a, b) => {
      const tA = getMenuTitle(a, isEn);
      const tB = getMenuTitle(b, isEn);
      return tA.localeCompare(tB, isEn ? 'en' : 'da');
    });

    bottom.sort((a, b) => {
      const tA = getMenuTitle(a, isEn);
      const tB = getMenuTitle(b, isEn);
      return tA.localeCompare(tB, isEn ? 'en' : 'da');
    });

    return { alphabeticalProducts: regular, bottomProducts: bottom };
  }, [productItems, isEn]);

  useEffect(() => {
    fetchLocalizedCollection('virksomhedsoplysninger', isEn ? 'en' : 'da').then((items) => {
      if (items && items.length) setCompanyInfo(items[0]);
    });
  }, [isEn]);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsOpen(false);
    setLubricationOpen(false);
    setAboutOpen(false);
  }, [location.pathname]);

  // Handle outside click for dropdowns
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setProductsOpen(false);
        setAboutOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  // Handle scroll shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isProductsActive = productItems.some((item) => {
    const daPath = item.sti;
    const enPath = getMenuHref(item.sti, true);
    return location.pathname === daPath || location.pathname === enPath;
  }) || [
    '/presser-2',
    '/brugte-maskiner',
    '/en/used-presses',
    '/en/used-machines',
    '/en/brugte-maskiner',
  ].includes(location.pathname);

  const isLubricationActive = isEn
    ? location.pathname === '/en/central-lubrication' ||
      location.pathname === '/en/centralsmoering'
    : location.pathname === '/centralsmoering';

  const isAboutActive = aboutItems.some((item) => {
    const daPath = item.sti;
    const enPath = getMenuHref(item.sti, true);
    return location.pathname === daPath || location.pathname === enPath;
  });

  // Localized URL helpers
  const homeUrl = isEn ? '/en' : '/';
  const productsUrl = isEn ? '/en/products' : '/produkter';
  const contactUrl = isEn ? '/en/contact' : '/kontakt';
  const lubricationUrl = isEn ? '/en/central-lubrication' : '/centralsmoering';
  const serviceUrl = isEn ? '/en/service' : '/service';

  return (
    <header
      ref={navRef}
      className={`sticky top-0 z-40 w-full bg-white transition-all duration-300 border-b border-stone-200 ${
        scrolled ? 'shadow-md shadow-stone-900/5' : ''
      }`}
    >
      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 transition-colors duration-200">
          {/* Logo area */}
          <Link
            to={homeUrl}
            className="flex items-center gap-3.5 group cursor-pointer"
            aria-label={
              isEn
                ? 'POVL MØLLERS MASKINFABRIK A/S - Home'
                : 'POVL MØLLERS MASKINFABRIK A/S - Forside'
            }
          >
            <div className="flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pmb-logo.png"
                alt="POVL MØLLERS MASKINFABRIK A/S"
                className="w-auto h-11 sm:h-12 md:h-13 object-contain"
                width={180}
                height={64}
              />
            </div>
          </Link>

          {/* Desktop Navigation (Visible only on screens >= 1200px) */}
          <nav
            className="hidden min-[1200px]:flex items-center gap-7 lg:gap-8"
            aria-label={isEn ? 'Main navigation' : 'Hovednavigation'}
          >
            {/* Forside / Home */}
            <NavLink
              to={homeUrl}
              end
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide py-2 transition-colors relative ${
                  isActive
                    ? 'text-[#006368] font-bold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#006368]'
                    : 'text-stone-700 hover:text-[#006368]'
                }`
              }
            >
              {isEn ? 'Home' : 'Forside'}
            </NavLink>

            {/* Produkter / Products Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <NavLink
                to={productsUrl}
                onClick={() => {
                  setProductsOpen(false);
                  setAboutOpen(false);
                  setLubricationOpen(false);
                }}
                className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors cursor-pointer ${
                  isProductsActive || productsOpen
                    ? 'text-[#006368] font-bold'
                    : 'text-stone-700 hover:text-[#006368]'
                }`}
              >
                <span>{isEn ? 'Products' : 'Produkter'}</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    productsOpen ? 'rotate-180 text-[#006368]' : 'text-stone-400'
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {productsOpen && (
                <div
                  className="absolute top-full left-0 w-80 bg-white rounded-lg border border-stone-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  role="menu"
                >
                  <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-400 border-b border-stone-100 flex items-center justify-between">
                    <span>{isEn ? 'Categories' : 'Kategorier'}</span>
                    <Link
                      to={productsUrl}
                      onClick={() => setProductsOpen(false)}
                      className="text-[#006368] hover:underline lowercase font-medium text-[10px]"
                    >
                      {isEn ? 'all products' : 'alle produkter'} →
                    </Link>
                  </div>

                  <div className="py-1">
                    {structuredProductMenu
                      .filter((cat) => cat.type !== 'bottom-link')
                      .map((entry) => {
                        if (entry.type === 'group') {
                          return (
                            <div key={entry.id} className="pt-2 pb-1">
                              {/* Non-clickable category group header */}
                              <div
                                className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5 select-none bg-stone-50/70"
                                aria-hidden="true"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#006368]" />
                                <span>{getMenuTitle(entry, isEn)}</span>
                              </div>
                              {/* Group subitems */}
                              <div className="space-y-0.5 mt-0.5">
                                {entry.items.map((sub) => (
                                  <NavLink
                                    key={sub.id}
                                    to={getMenuHref(sub.sti, isEn)}
                                    onClick={() => setProductsOpen(false)}
                                    className={({ isActive }) =>
                                      `block pl-7 pr-4 py-1.5 text-sm transition-colors ${
                                        isActive
                                          ? 'bg-[#006368]/10 text-[#006368] font-bold'
                                          : 'text-stone-700 hover:bg-stone-50 hover:text-[#006368]'
                                      }`
                                    }
                                    role="menuitem"
                                  >
                                    {getMenuTitle(sub, isEn)}
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Regular single category link
                        return (
                          <NavLink
                            key={entry.id}
                            to={getMenuHref(entry.sti, isEn)}
                            onClick={() => setProductsOpen(false)}
                            className={({ isActive }) =>
                              `block px-4 py-1.5 text-sm transition-colors ${
                                isActive
                                  ? 'bg-[#006368]/10 text-[#006368] font-bold'
                                  : 'text-stone-700 hover:bg-stone-50 hover:text-[#006368]'
                              }`
                            }
                            role="menuitem"
                          >
                            {getMenuTitle(entry, isEn)}
                          </NavLink>
                        );
                      })}
                  </div>

                  {/* Bottom pinned items: Brugte maskiner & Reservedele */}
                  <div className="border-t border-stone-200 mt-1 pt-1 bg-stone-50/40">
                    {structuredProductMenu
                      .filter((cat) => cat.type === 'bottom-link')
                      .map((entry) => (
                        <NavLink
                          key={entry.id}
                          to={getMenuHref(entry.sti, isEn)}
                          onClick={() => setProductsOpen(false)}
                          className={({ isActive }) =>
                            `block px-4 py-2 text-sm font-semibold transition-colors ${
                              isActive
                                ? 'bg-[#006368]/10 text-[#006368] font-bold'
                                : 'text-stone-900 hover:bg-stone-100 hover:text-[#006368]'
                            }`
                          }
                          role="menuitem"
                        >
                          {getMenuTitle(entry, isEn)}
                        </NavLink>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Smøring (Centralsmøring) / Lubrication Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={() => setLubricationOpen(true)}
              onMouseLeave={() => setLubricationOpen(false)}
            >
              <NavLink
                to={lubricationUrl}
                onClick={() => {
                  setProductsOpen(false);
                  setAboutOpen(false);
                }}
                className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors cursor-pointer ${
                  isLubricationActive || lubricationOpen
                    ? 'text-[#006368] font-bold'
                    : 'text-stone-700 hover:text-[#006368]'
                }`}
              >
                <span>{isEn ? 'Lubrication' : 'Smøring'}</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    lubricationOpen ? 'rotate-180 text-[#006368]' : 'text-stone-400'
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {lubricationOpen && (
                <div
                  className="absolute top-full left-0 w-72 bg-white rounded-lg border border-stone-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  role="menu"
                >
                  <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-400 border-b border-stone-100">
                    {isEn
                      ? 'Category: Central Lubrication'
                      : 'Kategori: Centralsmøring'}
                  </div>
                  <NavLink
                    to={lubricationUrl}
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-[#006368]/10 text-[#006368] font-bold'
                          : 'text-stone-700 hover:bg-stone-50 hover:text-[#006368]'
                      }`
                    }
                    role="menuitem"
                  >
                    <div className="font-semibold">
                      {isEn ? 'Central Lubrication' : 'Centralsmøring'}
                    </div>
                    <div className="text-xs text-stone-500 font-normal">
                      {isEn
                        ? 'Go to Central Lubrication landing page'
                        : 'Gå til Centralsmøring landingpage'}
                    </div>
                  </NavLink>
                </div>
              )}
            </div>

            {/* Service */}
            <NavLink
              to={serviceUrl}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide py-2 transition-colors relative ${
                  isActive
                    ? 'text-[#006368] font-bold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#006368]'
                    : 'text-stone-700 hover:text-[#006368]'
                }`
              }
            >
              Service
            </NavLink>

            {/* Om os / About us Dropdown */}
            <div
              className="relative py-2"
              onMouseEnter={() => setAboutOpen(true)}
              onMouseLeave={() => setAboutOpen(false)}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAboutOpen((prev) => !prev);
                  setProductsOpen(false);
                  setLubricationOpen(false);
                }}
                aria-expanded={aboutOpen}
                aria-haspopup="true"
                className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors cursor-pointer ${
                  isAboutActive || aboutOpen
                    ? 'text-[#006368] font-bold'
                    : 'text-stone-700 hover:text-[#006368]'
                }`}
              >
                <span>{isEn ? 'About us' : 'Om os'}</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    aboutOpen ? 'rotate-180 text-[#006368]' : 'text-stone-400'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {aboutOpen && (
                <div
                  className="absolute top-full left-0 w-64 bg-white rounded-lg border border-stone-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  role="menu"
                >
                  {aboutItems.map((item) => (
                    <NavLink
                      key={item.id}
                      to={getMenuHref(item.sti, isEn)}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'bg-[#006368]/10 text-[#006368] font-bold'
                            : 'text-stone-700 hover:bg-stone-50 hover:text-[#006368]'
                        }`
                      }
                      role="menuitem"
                    >
                      {getMenuTitle(item, isEn)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Kontakt / Contact */}
            <NavLink
              to={contactUrl}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide py-2 transition-colors relative ${
                  isActive
                    ? 'text-[#006368] font-bold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#006368]'
                    : 'text-stone-700 hover:text-[#006368]'
                }`
              }
            >
              {isEn ? 'Contact' : 'Kontakt'}
            </NavLink>
          </nav>

          {/* Desktop Right Action Area: Phone + CTA button + Language Switcher */}
          <div className="hidden min-[1200px]:flex items-center gap-5">
            <a
              href={`tel:${(companyInfo?.telefon || '57526262').replace(/\s+/g, '')}`}
              className="flex items-center gap-2 text-stone-800 hover:text-[#006368] transition font-semibold text-sm whitespace-nowrap"
            >
              <span className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-[#006368]">
                <i className="fa-solid fa-phone text-xs" />
              </span>
              <span className="whitespace-nowrap">{companyInfo?.telefon || '57 52 62 62'}</span>
            </a>

            <Link
              to={contactUrl}
              className="px-5 py-2.5 rounded bg-[#006368] hover:bg-[#004d51] text-white text-sm font-semibold tracking-wide transition shadow-sm cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <span>{isEn ? 'Contact us' : 'Kontakt os'}</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>

            {/* Language Switcher */}
            <LanguageSwitcher variant="desktop" />
          </div>

          {/* Mobile/Tablet Menu Button (Displays ONLY when screen width is UNDER 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2.5">
            <LanguageSwitcher variant="desktop" />

            <a
              href={`tel:${(companyInfo?.telefon || '57526262').replace(/\s+/g, '')}`}
              className="px-2.5 py-1.5 rounded border border-stone-200 bg-stone-50 text-[#006368] text-xs font-bold flex items-center gap-1.5 whitespace-nowrap"
              aria-label={
                isEn
                  ? `Call PMB at ${companyInfo?.telefon || '+45 57 52 62 62'}`
                  : `Ring til PMB på ${companyInfo?.telefon || '57 52 62 62'}`
              }
            >
              <i className="fa-solid fa-phone" />
              <span className="hidden sm:inline whitespace-nowrap">{companyInfo?.telefon || '57 52 62 62'}</span>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-label={
                mobileMenuOpen
                  ? isEn
                    ? 'Close menu'
                    : 'Luk menu'
                  : isEn
                  ? 'Open menu'
                  : 'Åbn menu'
              }
              className="p-2.5 rounded border border-stone-200 text-stone-700 hover:text-[#006368] hover:border-[#006368] transition cursor-pointer"
            >
              <i
                className={`fa-solid ${
                  mobileMenuOpen ? 'fa-xmark' : 'fa-bars'
                } text-lg`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-stone-200 bg-white shadow-2xl animate-in slide-in-from-top-2 duration-200">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-4 max-h-[80vh] overflow-y-auto">
            {/* Mobile Language Switcher row */}
            <div className="pb-3 border-b border-stone-100">
              <LanguageSwitcher variant="mobile" />
            </div>

            <NavLink
              to={homeUrl}
              end
              className={({ isActive }) =>
                `block px-3 py-2 rounded text-base font-semibold ${
                  isActive
                    ? 'bg-[#006368]/10 text-[#006368] font-bold'
                    : 'text-stone-800 hover:bg-stone-50'
                }`
              }
            >
              {isEn ? 'Home' : 'Forside'}
            </NavLink>

            {/* Mobile Produkter / Products accordion */}
            <div>
              <div className="flex items-center justify-between px-3 py-2 rounded text-base font-semibold text-stone-800 hover:bg-stone-50">
                <Link
                  to={productsUrl}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-stone-800 hover:text-[#006368]"
                >
                  {isEn ? 'Products' : 'Produkter'}
                </Link>
                <button
                  type="button"
                  onClick={() => setProductsOpen((prev) => !prev)}
                  aria-label={productsOpen ? 'Luk produktmenu' : 'Åbn produktmenu'}
                  className="p-1 text-stone-500 hover:text-[#006368] cursor-pointer"
                >
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform ${
                      productsOpen ? 'rotate-180 text-[#006368]' : 'text-stone-400'
                    }`}
                  />
                </button>
              </div>
              {productsOpen && (
                <div className="pl-4 pr-2 py-2 space-y-1 bg-stone-50 rounded mt-1 border-l-2 border-[#006368]">
                  <Link
                    to={productsUrl}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-1.5 text-xs font-bold text-[#006368] uppercase tracking-wider"
                  >
                    {isEn ? '→ All products & categories' : '→ Alle produkter & kategorier'}
                  </Link>

                  {structuredProductMenu
                    .filter((cat) => cat.type !== 'bottom-link')
                    .map((entry) => {
                      if (entry.type === 'group') {
                        return (
                          <div key={entry.id} className="pt-2 pb-1">
                            <div className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5 select-none">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#006368]" />
                              <span>{getMenuTitle(entry, isEn)}</span>
                            </div>
                            <div className="pl-3 space-y-0.5">
                              {entry.items.map((sub) => (
                                <NavLink
                                  key={sub.id}
                                  to={getMenuHref(sub.sti, isEn)}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={({ isActive }) =>
                                    `block px-3 py-1.5 text-sm rounded ${
                                      isActive
                                        ? 'bg-[#006368]/10 text-[#006368] font-bold'
                                        : 'text-stone-700 hover:text-[#006368]'
                                    }`
                                  }
                                >
                                  {getMenuTitle(sub, isEn)}
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <NavLink
                          key={entry.id}
                          to={getMenuHref(entry.sti, isEn)}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            `block px-3 py-1.5 text-sm rounded ${
                              isActive
                                ? 'bg-[#006368]/10 text-[#006368] font-bold'
                                : 'text-stone-700 hover:text-[#006368]'
                            }`
                          }
                        >
                          {getMenuTitle(entry, isEn)}
                        </NavLink>
                      );
                    })}

                  <div className="border-t border-stone-200 my-1 pt-1" />
                  {structuredProductMenu
                    .filter((cat) => cat.type === 'bottom-link')
                    .map((entry) => (
                      <NavLink
                        key={entry.id}
                        to={getMenuHref(entry.sti, isEn)}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `block px-3 py-2 text-sm font-bold rounded ${
                            isActive
                              ? 'bg-[#006368]/10 text-[#006368]'
                              : 'text-stone-900 hover:text-[#006368]'
                          }`
                        }
                      >
                        {getMenuTitle(entry, isEn)}
                      </NavLink>
                    ))}
                </div>
              )}
            </div>

            {/* Mobile Smøring (Centralsmøring) / Lubrication accordion */}
            <div>
              <button
                type="button"
                onClick={() => setLubricationOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-3 py-2 rounded text-base font-semibold text-stone-800 hover:bg-stone-50 cursor-pointer"
              >
                <span>{isEn ? 'Lubrication' : 'Smøring'}</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform ${
                    lubricationOpen
                      ? 'rotate-180 text-[#006368]'
                      : 'text-stone-400'
                  }`}
                />
              </button>
              {lubricationOpen && (
                <div className="pl-4 pr-2 py-2 space-y-1 bg-stone-50 rounded mt-1 border-l-2 border-[#006368]">
                  <NavLink
                    to={lubricationUrl}
                    className="block px-3 py-2 text-sm text-stone-700 hover:text-[#006368]"
                  >
                    {isEn
                      ? 'Central Lubrication (Landing Page)'
                      : 'Centralsmøring (Landingpage)'}
                  </NavLink>
                </div>
              )}
            </div>

            {/* Mobile Service */}
            <NavLink
              to={serviceUrl}
              className={({ isActive }) =>
                `block px-3 py-2 rounded text-base font-semibold ${
                  isActive
                    ? 'bg-[#006368]/10 text-[#006368] font-bold'
                    : 'text-stone-800 hover:bg-stone-50'
                }`
              }
            >
              Service
            </NavLink>

            {/* Mobile Om os / About us accordion */}
            <div>
              <button
                type="button"
                onClick={() => setAboutOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-3 py-2 rounded text-base font-semibold text-stone-800 hover:bg-stone-50 cursor-pointer"
              >
                <span>{isEn ? 'About us' : 'Om os'}</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform ${
                    aboutOpen ? 'rotate-180 text-[#006368]' : 'text-stone-400'
                  }`}
                />
              </button>
              {aboutOpen && (
                <div className="pl-4 pr-2 py-2 space-y-1 bg-stone-50 rounded mt-1 border-l-2 border-[#006368]">
                  {aboutItems.map((item) => (
                    <NavLink
                      key={item.id}
                      to={getMenuHref(item.sti, isEn)}
                      className="block px-3 py-2 text-sm text-stone-700 hover:text-[#006368]"
                    >
                      {getMenuTitle(item, isEn)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to={contactUrl}
              className={({ isActive }) =>
                `block px-3 py-2 rounded text-base font-semibold ${
                  isActive
                    ? 'bg-[#006368]/10 text-[#006368] font-bold'
                    : 'text-stone-800 hover:bg-stone-50'
                }`
              }
            >
              {isEn ? 'Contact' : 'Kontakt'}
            </NavLink>

            <div className="pt-4 border-t border-stone-200 space-y-3">
              <Link
                to={contactUrl}
                className="block w-full text-center px-4 py-3 rounded bg-[#006368] text-white font-semibold text-sm shadow-sm"
              >
                {isEn ? 'Contact us for a dialogue' : 'Kontakt os for en dialog'}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
